<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowMapNode from './FlowMapNode.vue';

// Secondary, collapsed-by-default overview. It only draws screens reachable
// from the greeting (root-only traversal), which is fine now that ScreenList is
// the authoritative navigator; the collapsed header still surfaces the count of
// screens that are NOT on the path so nothing is silently missed.
const props = defineProps({
  screens: { type: Array, default: () => [] },
  currentId: { type: String, default: '' },
});
defineEmits(['select']);
const { t } = useI18n();

const open = ref(false);

const byId = computed(() =>
  props.screens.reduce((acc, s) => {
    acc[s.id] = s;
    return acc;
  }, {})
);

const rootScreen = computed(() => props.screens.find(s => s.isRoot));
const rootHasButtons = computed(() =>
  (rootScreen.value?.buttons || []).some(b => b.target)
);
const orphanCount = computed(() => {
  const referenced = new Set(
    props.screens
      .flatMap(s => (s.buttons || []).map(b => b.target))
      .filter(Boolean)
  );
  return props.screens.filter(
    s => !s.isRoot && s.title.trim() && !referenced.has(s.id)
  ).length;
});

// Build a plain nested tree from the screen graph, collapsing already-seen
// (shared / cyclic) screens into a 'shared' reference so recursion terminates.
const tree = computed(() => {
  const root = props.screens.find(s => s.isRoot);
  if (!root) return null;
  const seen = new Set([root.id]);
  const build = (screen, isRoot) => {
    const node = {
      kind: 'screen',
      id: screen.id,
      isRoot,
      title: isRoot ? t('AUTO_REPLY_BOT_SETTINGS.MAP.START') : screen.title,
      count: (screen.buttons || []).filter(
        b => b.target || (b.label || '').trim()
      ).length,
      children: [],
    };
    (screen.buttons || []).forEach(b => {
      const target = b.target && byId.value[b.target];
      if (target) {
        if (seen.has(target.id)) {
          node.children.push({
            kind: 'shared',
            id: target.id,
            title: target.title,
          });
        } else {
          seen.add(target.id);
          node.children.push(build(target, false));
        }
      } else if ((b.label || '').trim()) {
        node.children.push({ kind: 'leaf', label: b.label });
      }
    });
    return node;
  };
  return build(root, true);
});
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
    <button
      type="button"
      class="flex w-full items-center gap-2.5 text-right"
      @click="open = !open"
    >
      <span class="i-lucide-git-fork size-[18px] rotate-180 text-n-brand" />
      <h3 class="text-sm font-extrabold text-n-slate-12">
        {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.TOGGLE') }}
      </h3>
      <span class="text-xs font-medium text-n-slate-10">
        {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.SUB') }}
      </span>
      <span
        v-if="orphanCount > 0"
        class="rounded-full bg-n-amber-3 px-2 py-0.5 text-[10.5px] font-extrabold text-n-amber-11"
      >
        {{
          t('AUTO_REPLY_BOT_SETTINGS.MAP.ORPHAN_COUNT', { count: orphanCount })
        }}
      </span>
      <span
        class="i-lucide-chevron-down ms-auto size-4 text-n-slate-10 transition-transform"
        :class="{ 'rotate-180': open }"
      />
    </button>

    <template v-if="open">
      <p
        v-if="!rootHasButtons"
        class="mt-4 rounded-xl border border-dashed border-n-weak px-3 py-4 text-center text-[12px] font-medium text-n-slate-10"
      >
        {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.EMPTY') }}
      </p>

      <!-- dot-grid canvas, like real flow builders -->
      <div
        v-else
        class="mt-4 overflow-x-auto rounded-2xl border border-n-weak/60 bg-[radial-gradient(circle,_rgb(var(--slate-4))_1.1px,_transparent_1.2px)] bg-n-alpha-1 px-5 pb-5 pt-6 [background-size:19px_19px]"
      >
        <FlowMapNode
          v-if="tree"
          :node="tree"
          :current-id="currentId"
          @select="$emit('select', $event)"
        />
      </div>

      <!-- legend -->
      <div
        class="mt-3.5 flex flex-wrap gap-3.5 border-t border-n-weak/60 pt-3.5 text-[11px] font-semibold text-n-slate-10"
      >
        <span class="inline-flex items-center gap-1.5">
          <span class="size-2.5 rounded bg-n-brand" />
          {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.LEGEND_CURRENT') }}
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="size-2.5 rounded bg-n-alpha-3" />
          {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.LEGEND_SHARED') }}
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span
            class="size-2.5 rounded border border-dashed border-n-slate-8"
          />
          {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.LEGEND_LEAF') }}
        </span>
      </div>
    </template>
  </div>
</template>
